import { motion } from "framer-motion";
import { Camera, Facebook, Mail } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const PHOTOS = [
  { src: "/wreck-bass.jpg", caption: "A proper wreck-caught bass", tag: "Wreck Fishing" },
  { src: "/bream-catch.jpg", caption: "Bream over the marks", tag: "Bream Fishing" },
  { src: "/mackerel-catch.jpg", caption: "First mackerel of the day", tag: "Mackerel Trip" },
  { src: "/boat-underway.jpg", caption: "Shande III heading out from Exmouth Marina", tag: "The Boat" },
  { src: "/boat-sunset.jpg", caption: "Early start, flat calm", tag: "The Boat" },
  { src: "/stag-rut.jpg", caption: "Rutting stags - deer stalking mornings", tag: "Coming Soon" },
  { src: "/mcnab-stag.jpg", caption: "A Devon stag in velvet - part of the McNab", tag: "Coming Soon" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5, ease: "easeOut" } }),
};

export default function Gallery() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="gallery-page">
      <Navbar />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <p className="section-eyebrow">Anglers Gallery</p>
          <h1 className="mt-2 font-display text-5xl font-black uppercase tracking-tight text-slate-900 sm:text-6xl" data-testid="gallery-headline">
            Caught Aboard <span className="text-sky-500">Shande III</span>
          </h1>
          <p className="mt-3 max-w-xl text-slate-500">
            Real fish, real anglers, real days out of Exmouth. No stock photos here.
          </p>
        </motion.div>

        <div className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
          {PHOTOS.map((p, i) => (
            <motion.figure key={p.src} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              data-testid={`gallery-item-${i}`}
              className="group relative mb-5 break-inside-avoid overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm shadow-slate-900/5">
              <img src={p.src} alt={p.caption} loading="lazy"
                className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 bg-gradient-to-t from-slate-900/80 to-transparent p-4 pt-10 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <span className="text-sm font-medium text-white">{p.caption}</span>
                <span className="shrink-0 rounded-full bg-sky-500/90 px-2.5 py-1 text-xs font-bold text-white">{p.tag}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
          className="card-clean mt-12 flex flex-col items-start gap-4 p-8 md:flex-row md:items-center md:justify-between" data-testid="gallery-share">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sky-100">
              <Camera className="h-6 w-6 text-sky-600" />
            </span>
            <div>
              <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-slate-900">Caught Something Worth Showing?</h2>
              <p className="mt-1 text-sm text-slate-500">Send us your photo and your fish could star in the gallery.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href="mailto:shandefishingltd@gmail.com" data-testid="gallery-share-email"
              className="inline-flex items-center gap-2 rounded-full bg-sky-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-sky-600">
              <Mail className="h-4 w-4" /> Email your catch
            </a>
            <a href="https://www.facebook.com/share/1TQ34JFFoY/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" data-testid="gallery-share-facebook"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:border-sky-400 hover:text-sky-600">
              <Facebook className="h-4 w-4" /> Tag us on Facebook
            </a>
          </div>
        </motion.div>
      </section>
      <Footer />
    </div>
  );
}
